import type { ApiClientResponse } from "@lobby/api-client";
import { useTranslate } from "@lobby/ocb-intl";
import { Link } from "@tanstack/react-router";
import { clsx } from "clsx";
import JsBarcode from "jsbarcode";
import { type MouseEvent, type PropsWithChildren, useEffect, useRef, useState } from "react";
import { MemberCard as MemberCardModel } from "../../../../../entities";
import { Button, Icon, Skeleton, chunkString, useLobbyLogo } from "../../../../../shared";
import { formatPin } from "./helpers";

const DEFAULT_BARCODE_VALUE = "0000000000000000";

function CardBase({
  className,
  children,
  onClick,
}: PropsWithChildren<{ className?: string; onClick?: () => void }>) {
  return (
    <div className={clsx("member-card--card", className)} onClick={onClick}>
      {children}
    </div>
  );
}

export function CardFront({ onClick }: { onClick: () => void }) {
  const Logo = useLobbyLogo();
  return (
    <CardBase className="member-card--card-front" onClick={onClick}>
      <Logo />
    </CardBase>
  );
}

export function CardBack() {
  const [pinValue, setPinValue] = useState("000-000");
  const pinLoadingIndicatorRef = useRef<HTMLDivElement>(null);

  const { $t } = useTranslate();

  const { data: memberCard, isFetching } = MemberCardModel.useCurrent();
  const { mutate: generatePin, isPending: isPinGenerationPending } =
    MemberCardModel.usePinGenerator();

  const barcodeValue = memberCard?.memberCard || DEFAULT_BARCODE_VALUE;

  function handlePinGenerationSuccess({ result }: ApiClientResponse<"MemberCard.generatePin">) {
    if (result) {
      setPinValue(formatPin(result));
    }
  }

  function handlePinGeneration(e: MouseEvent<HTMLDivElement>) {
    e.stopPropagation();
    generatePin(undefined, { onSuccess: handlePinGenerationSuccess });
  }

  useEffect(() => {
    if (memberCard?.pin) {
      setPinValue(formatPin(memberCard.pin));
    }
  }, [memberCard?.pin]);

  useEffect(() => {
    if (isPinGenerationPending && pinLoadingIndicatorRef.current) {
      pinLoadingIndicatorRef.current.classList.add("animate-spin");
    }
  }, [isPinGenerationPending]);

  useEffect(() => {
    JsBarcode("#member-card-barcode", barcodeValue, {
      background: "transparent",
      font: "Ubuntu",
      fontOptions: "bold",
      fontSize: 12,
      textMargin: 8,
      margin: 0,
      height: 150,
      displayValue: false,
    });
  }, [barcodeValue]);

  return (
    <CardBase className="member-card--card-back">
      <div className="absolute inset-0 flex-center flex-col gap-[0.5em] py-[1.5em]">
        <div className="flex h-[1em] min-h-0 items-center gap-[0.25em] font-bold">
          <span className="text-[0.75em]">{$t({ defaultMessage: "PIN" })}:</span>
          {isFetching || isPinGenerationPending ? (
            <Skeleton className="h-full w-[2.8125em]" />
          ) : (
            <span className="text-[0.75em]">{pinValue}</span>
          )}
          <div
            ref={pinLoadingIndicatorRef}
            onClick={handlePinGeneration}
            onAnimationIteration={(e) =>
              !isPinGenerationPending && e.currentTarget.classList.remove("animate-spin")
            }
            className="relative inline-block size-[1em] cursor-pointer"
          >
            <Icon name="reload" />
          </div>
        </div>
        <svg className="mx-auto min-h-0 w-auto max-w-full grow" id="member-card-barcode" />
        {isFetching ? (
          <Skeleton className="h-[1em] w-1/2" />
        ) : (
          <div className="h-[1em] min-h-0 font-bold">
            <div className="text-[0.75em] leading-none">
              {chunkString(barcodeValue, 4)?.join("-")}
            </div>
          </div>
        )}
      </div>
    </CardBase>
  );
}

export function LogInWithBarcodeButton({ onClick }: { onClick: () => void }) {
  const { $t } = useTranslate();
  return (
    <Button variant="secondary" onClick={onClick} className="whitespace-normal">
      {$t({ defaultMessage: "log in to the kiosk using barcode" })}
    </Button>
  );
}

export function BackToLobbyLink() {
  const { $t } = useTranslate();

  return (
    <Link to="/" className="mt-6 w-fit font-bold text-14 uppercase">
      {$t({ defaultMessage: "back to the lobby" })}
    </Link>
  );
}

export function AddCardHint({ className }: { className?: string }) {
  const { $t } = useTranslate();

  return (
    <p className={clsx("mt-4 font-medium text-12 leading-6 tracking-wide", className)}>
      {$t(
        {
          defaultMessage:
            'Add the virtual Member Card to the home screen. In browser options press {icon} and tap "Add to home screen"!',
        },
        {
          icon: (
            <svg className="inline-block size-4" fill="none" viewBox="0 0 42 49">
              <path stroke="currentColor" strokeWidth="2" d="M12.96 15H1v33h40V15H28.66" />
              <path
                fill="currentColor"
                d="M21.7.3a1 1 0 0 0-1.4 0l-6.37 6.36a1 1 0 0 0 1.41 1.41L21 2.41l5.66 5.66a1 1 0 1 0 1.41-1.41L21.71.29ZM22 29V1h-2v28h2Z"
              />
            </svg>
          ),
        },
      )}
    </p>
  );
}
